// src/components/scenes/SceneEditor.js
import React, { useState, useContext } from 'react';
import { FaLightbulb, FaMusic, FaChevronUp, FaChevronDown, FaEdit, FaTrash } from 'react-icons/fa';
import { AppContext } from '../../context/AppContext';
import DeviceActionEditor from './DeviceActionEditor';
import SoundActionEditor from './SoundActionEditor';
import { toast } from 'react-toastify';
import './SceneEditor.css';

const SceneEditor = ({ scene, onClose, onSave }) => {
  const { createScene, updateScene, devices } = useContext(AppContext);
  const [sceneData, setSceneData] = useState({
    id: '',
    name: '',
    description: '',
    category: 'Home',
    actions: [],
    ...scene
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showActionEditor, setShowActionEditor] = useState(false);
  const [currentActionIndex, setCurrentActionIndex] = useState(-1);
  
  // Get unique categories from devices for suggestions
  const getUniqueCategories = () => {
    const categories = new Set(devices.map(device => device.location).filter(Boolean));
    return ['Home', 'Living Room', 'Bedroom', 'Kitchen', 'Office', ...Array.from(categories)];
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSceneData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!sceneData.name.trim()) {
      toast.error('Please enter a scene name');
      return;
    }
    
    try {
      setIsLoading(true);
      
      let savedScene;
      if (scene && scene.id) {
        // Update existing scene
        savedScene = await updateScene(scene.id, sceneData);
        toast.success(`Scene "${savedScene.name}" updated successfully`);
      } else {
        // Create new scene
        savedScene = await createScene(sceneData);
        toast.success(`Scene "${savedScene.name}" created successfully`);
      }
      
      if (onSave) {
        onSave(savedScene);
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving scene:', error);
      toast.error('Failed to save scene');
    } finally {
      setIsLoading(false);
    }
  };
  
  // States for action editor type
  const [actionEditorType, setActionEditorType] = useState('device');
  
  // Add a new action
  const addAction = (type = 'device') => {
    setCurrentActionIndex(-1);
    setActionEditorType(type);
    setShowActionEditor(true);
  };
  
  // Edit an existing action
  const editAction = (index) => {
    setCurrentActionIndex(index);
    // Determine the type of action to edit
    const actionType = sceneData.actions[index]?.type || 'device';
    setActionEditorType(actionType);
    setShowActionEditor(true);
  };
  
  // Delete an action
  const deleteAction = (index) => {
    setSceneData(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }));
  };
  
  // Handle saving an action
  const saveAction = (action) => {
    setSceneData(prev => {
      const newActions = [...prev.actions];
      
      if (currentActionIndex >= 0) {
        // Update existing action
        newActions[currentActionIndex] = action;
      } else {
        // Add new action
        newActions.push(action);
      }
      
      return {
        ...prev,
        actions: newActions
      };
    });
    
    setShowActionEditor(false);
  };
  
  // Move an action up or down in the list
  const moveAction = (index, direction) => {
    if (direction !== 'up' && direction !== 'down') return;
    
    setSceneData(prev => {
      const newActions = [...prev.actions];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      
      if (newIndex < 0 || newIndex >= newActions.length) return prev;
      
      // Swap actions
      const temp = newActions[index];
      newActions[index] = newActions[newIndex];
      newActions[newIndex] = temp;
      
      return {
        ...prev,
        actions: newActions
      };
    });
  };
  
  return (
    <div className="scene-editor">
      {showActionEditor ? (
        actionEditorType === 'device' ? (
          <DeviceActionEditor 
            action={currentActionIndex >= 0 ? sceneData.actions[currentActionIndex] : null}
            devices={devices}
            onSave={saveAction}
            onCancel={() => setShowActionEditor(false)}
          />
        ) : actionEditorType === 'sound' ? (
          <SoundActionEditor
            action={currentActionIndex >= 0 ? sceneData.actions[currentActionIndex] : null}
            onSave={saveAction}
            onCancel={() => setShowActionEditor(false)}
          />
        ) : (
          // Fallback to device editor for other types
          <DeviceActionEditor 
            action={currentActionIndex >= 0 ? sceneData.actions[currentActionIndex] : null}
            devices={devices}
            onSave={saveAction}
            onCancel={() => setShowActionEditor(false)}
          />
        )
      ) : (
        <form onSubmit={handleSubmit}>
          <h2>{scene && scene.id ? 'Edit Scene' : 'Create New Scene'}</h2>
          
          <div className="form-group">
            <label htmlFor="name">Scene Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={sceneData.name}
              onChange={handleInputChange}
              placeholder="Enter scene name"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={sceneData.description}
              onChange={handleInputChange}
              placeholder="Enter scene description"
              rows={3}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="category">Category</label>
            <select
              id="category"
              name="category"
              value={sceneData.category}
              onChange={handleInputChange}
            >
              {getUniqueCategories().map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          
          <div className="scene-actions-editor">
            <h3>Actions</h3>
            
            {sceneData.actions.length === 0 ? (
              <div className="empty-actions">
                <p>No actions have been added to this scene yet.</p>
              </div>
            ) : (
              <ul className="actions-list">
                {sceneData.actions.map((action, index) => (
                  <li key={index} className="action-item">
                    <div className="action-content">
                      <span className="action-number">{index + 1}.</span>
                      <span className="action-type">{action.type}</span>
                      <span className="action-description">
                        {getActionDescription(action)}
                      </span>
                    </div>
                    
                    <div className="action-controls">
                      <button
                        type="button"
                        className="move-up-btn"
                        onClick={() => moveAction(index, 'up')}
                        disabled={index === 0}
                      >
                        <FaChevronUp />
                      </button>
                      
                      <button
                        type="button"
                        className="move-down-btn"
                        onClick={() => moveAction(index, 'down')}
                        disabled={index === sceneData.actions.length - 1}
                      >
                        <FaChevronDown />
                      </button>
                      
                      <button
                        type="button"
                        className="edit-action-btn"
                        onClick={() => editAction(index)}
                      >
                        <FaEdit />
                      </button>
                      
                      <button
                        type="button"
                        className="delete-action-btn"
                        onClick={() => deleteAction(index)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            
            <div className="action-buttons">
              <button
                type="button"
                className="add-action-btn"
                onClick={() => addAction('device')}
              >
<FaLightbulb />
                <span>Add Device Action</span>
              </button>
              
              <button
                type="button"
                className="add-action-btn sound-action"
                onClick={() => addAction('sound')}
              >
<FaMusic />
                <span>Add Sound</span>
              </button>
            </div>
          </div>
          
          <div className="editor-controls">
            <button 
              type="button"
              className="cancel-btn"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            
            <button 
              type="submit"
              className="save-btn"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="spinner-sm"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <span>Save Scene</span>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

// Helper function to get human-readable action description
const getActionDescription = (action) => {
  switch (action.type) {
    case 'sound':
      return `Play sound: ${action.sound}`;
    case 'device':
      if (action.payload) {
        return `Control ${action.device}: ${JSON.stringify(action.payload)}`;
      }
      return `Control ${action.device}: ${action.command || ''} ${action.value || ''}`;
    case 'delay':
      return `Wait for ${action.duration} ms`;
    case 'notification':
      return `Notification: ${action.message}`;
    default:
      return `${action.type}: ${JSON.stringify(action)}`;
  }
};

export default SceneEditor;